import { useEffect, useRef, useState } from 'react';
import { countryFlag, normalizeCity } from 'shared';
import { api, type Place } from './api';
import Icon from './Icon';
import MapPointPicker from './MapPointPicker';
import Modal from './Modal';
import { haptic } from './telegram';

export interface PlaceValue {
  address: string;
  region: string;
  country: string | null;
  lat: number;
  lng: number;
}

interface Props {
  label: string;
  value: PlaceValue | null;
  onChange: (p: PlaceValue) => void;
}

type Tab = 'search' | 'map';

/** Флаг из кода страны — считается по Unicode, поэтому знает не только СНГ. */
function flagOf(code: string | null): string {
  const flag = countryFlag(code);
  return flag ? `${flag} ` : '';
}

/**
 * Выбор места отгрузки или доставки.
 *
 * Пришёл на смену выбору из захардкоженного списка городов. Тот список
 * был границей возможного: деревни, промзоны и любого города, который
 * забыли внести, для площадки просто не существовало — а перевозки
 * случаются не только между областными центрами.
 *
 * Три способа, потому что ни один не покрывает всё: поиск по названию
 * (быстро, когда название знаешь), точка на карте (когда названия нет
 * или оно ничего не даёт — карьер, элеватор, съезд с трассы) и ручной
 * ввод названия поверх найденной точки (когда местное название не
 * совпадает с тем, что знает геокодер).
 */
export default function PlacePicker({ label, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('search');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Place[]>([]);
  const [loading, setLoading] = useState(false);

  /** Точка, выбранная на карте, — ждёт подтверждения названия. */
  const [pending, setPending] = useState<PlaceValue | null>(null);
  const seq = useRef(0);

  // Поиск с паузой: без неё каждый символ уходит во внешний геокодер,
  // а он на бесплатной квоте.
  useEffect(() => {
    if (!open || tab !== 'search') return;
    const q = query.trim();
    if (q.length < 2) {
      setItems([]);
      return;
    }
    const id = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .geoSearch(q)
        .then((r) => {
          if (id === seq.current) setItems(r.items);
        })
        .catch(() => id === seq.current && setItems([]))
        .finally(() => id === seq.current && setLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query, open, tab]);

  function openPicker() {
    setQuery('');
    setItems([]);
    setPending(null);
    setTab('search');
    setOpen(true);
  }

  function commit(p: PlaceValue) {
    const address = normalizeCity(p.address);
    if (!address) return;
    haptic('light');
    onChange({ ...p, address });
    setOpen(false);
  }

  /** Клик по карте: спрашиваем геокодер, как это место называется. */
  function onMapPick(pt: { lat: number; lng: number }) {
    setPending({ address: '', region: '', country: null, ...pt });
    api
      .geoReverse(pt.lat, pt.lng)
      .then((r) => {
        setPending((prev) =>
          prev && prev.lat === pt.lat && prev.lng === pt.lng
            ? {
                ...prev,
                address: r.place?.name ?? '',
                region: r.place?.region ?? '',
                country: r.place?.country ?? null,
              }
            : prev,
        );
      })
      .catch(() => {
        // Геокодер молчит — точка всё равно годная, название введут руками.
      });
  }

  return (
    <>
      <button type="button" className="picker-trigger" onClick={openPicker}>
        <span className="picker-trigger__value">
          {value ? `${flagOf(value.country)}${value.address}` : 'Не выбрано'}
        </span>
        <Icon name="down" size={16} />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={label}>
        <div className="seg" style={{ marginBottom: 'var(--s3)' }}>
          <button
            type="button"
            className={tab === 'search' ? 'seg__btn seg__btn--on' : 'seg__btn'}
            onClick={() => setTab('search')}
          >
            Поиск
          </button>
          <button
            type="button"
            className={tab === 'map' ? 'seg__btn seg__btn--on' : 'seg__btn'}
            onClick={() => setTab('map')}
          >
            На карте
          </button>
        </div>

        {tab === 'search' && (
          <>
            <div className="field">
              <input
                placeholder="Город, посёлок, станция…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="picker-scroll">
              {loading && <div className="meta">Ищу…</div>}

              {!loading && query.trim().length >= 2 && items.length === 0 && (
                <div className="empty">
                  Ничего не нашлось. Укажите точку на карте — название можно
                  будет вписать своё.
                </div>
              )}

              <div className="menu-list">
                {items.map((p) => (
                  <button
                    key={`${p.name}-${p.lat}-${p.lng}`}
                    type="button"
                    className="menu-item"
                    onClick={() =>
                      commit({
                        address: p.name,
                        region: p.region,
                        country: p.country || null,
                        lat: p.lat,
                        lng: p.lng,
                      })
                    }
                  >
                    {flagOf(p.country || null)}
                    {p.name}
                    <span className="meta" style={{ marginLeft: 'auto' }}>
                      {p.region}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'map' && (
          <>
            <MapPointPicker
              start={value ? { lat: value.lat, lng: value.lng } : null}
              onPick={onMapPick}
            />

            {pending && (
              <div style={{ marginTop: 'var(--s3)' }}>
                <div className="field">
                  <label>Как назвать это место</label>
                  <input
                    placeholder="Например: карьер у Кстова"
                    value={pending.address}
                    onChange={(e) => setPending({ ...pending, address: e.target.value })}
                  />
                </div>
                <div className="meta" style={{ marginBottom: 8 }}>
                  {pending.region || 'регион не определён'} ·{' '}
                  {pending.lat.toFixed(4)}, {pending.lng.toFixed(4)}
                </div>
                <button disabled={!pending.address.trim()} onClick={() => commit(pending)}>
                  Выбрать это место
                </button>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
